import React from "react";
import type { Box } from "./frame";
import type { Theme } from "./theme";
import { AtomVisual, MoleculeVisual, findElement, findMolecule, findMoleculeByFormula, findMoleculeByName } from "./visuals-science";
import { Icon, iconForLabel } from "./icons";
import { TerminalActor } from "./visuals-code";
import { ScenePlate, StickerCutout, AssetPending } from "./visuals-images";
import { EngineActor } from "./visuals-machines";
import { BioActor, BIO_RE, parseBioConfig } from "./visuals-biology";
import { CircuitActor, CIRCUIT_RE, parseCircuitConfig } from "./visuals-circuit";
import { GeometryActor, GEOMETRY_RE, parseGeometryConfig } from "./visuals-geometry";
import { ChartActor, parseChartSpec } from "./visuals-charts";
import { NeuralActor, NEURAL_RE, parseNeuralConfig } from "./visuals-neural";
import { parseMascotConfig } from "./visuals-mascot";
import { IsoPipelineActor } from "./visuals-iso";
import { MascotActor } from "./visuals-mascot";
import { ArchFlowActor } from "./visuals-archflow";
import { RhythmActor, RHYTHM_RE } from "./visuals-rhythm";
import { settle as animSettle, settleScale as animSettleScale } from "./anim";

/**
 * Anivaze v4 — animated CONCEPT VISUALS.
 *
 * The difference between a slideshow and an explainer: each beat gets a diagram
 * that CONTINUOUSLY MOVES and illustrates the idea — tokens flowing through a
 * pipeline, pulses propagating a network, a cycle sweeping, a graph drawing
 * itself. All pure functions of `t` (deterministic → preview === export), with
 * continuous/looping motion (Kurzgesagt-style life) plus build-on reveals
 * (3Blue1Brown-style progressive disclosure). See PLAN §6–7.
 */

export type VisualKind = "flow" | "network" | "cycle" | "grow" | "compare" | "atom" | "molecule" | "engine" | "bio" | "circuit" | "geometry" | "chart" | "neural" | "pipeline" | "archflow" | "rhythm" | "mascot" | "terminal" | "image" | "sticker" | "none";

const TAU = Math.PI * 2;
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
// tiny deterministic hash → [0,1)
const rnd = (i: number, s = 1) => {
  const x = Math.sin((i + 1) * 12.9898 * s + 78.233) * 43758.5453;
  return x - Math.floor(x);
};
const trunc = (s: string, n: number) => (s.length > n ? s.slice(0, n - 1).trimEnd() + "…" : s);
const pointOnLine = (ax: number, ay: number, bx: number, by: number, f: number) => ({ x: lerp(ax, bx, f), y: lerp(ay, by, f) });

/** Mechanical-subject signal — a piston beat must get the ENGINE, not boxes. */
export const ENGINE_RE = /piston|crankshaft|crankcase|spark plug|\b(2|two|4|four)[\s-]?strokes?\b|camshaft|\bcylinder\b|combustion chamber|connecting rod|\btdc\b|\bbdc\b|dead cent|transfer port|exhaust port|intake port|flywheel/i;

/**
 * Upgrade generic diagram kinds to a real machine actor when the beat is
 * clearly about one — the AI may say "flow", but a piston beat must SHOW the
 * piston. Specific actors (terminal/atom/…) are never overridden.
 */
export function maybeUpgradeKind(kind: VisualKind, text: string): VisualKind {
  const generic = kind === "flow" || kind === "compare" || kind === "cycle" || kind === "network" || kind === "grow";
  if (generic && ENGINE_RE.test(text)) return "engine";
  // A sperm/egg/cell-division/heartbeat beat must SHOW the subject, not a dot
  // on a line — the exact failure the reference study pins (§3).
  if (generic && BIO_RE.test(text)) return "bio";
  // Electrical beats must SHOW the circuit (battery→load→switch, current flowing).
  if (generic && CIRCUIT_RE.test(text)) return "circuit";
  // Shapes/geometry must SHOW the figure (point/line/angle/triangle/pythagoras…),
  // never a dotted rail of chips — the exact reported failure.
  if (generic && GEOMETRY_RE.test(text)) return "geometry";
  // Neural-net / ML beats must SHOW the layered network (forward pass, backprop
  // loop), not 3 boxes on a rail or a hub-and-spoke.
  if (generic && NEURAL_RE.test(text)) return "neural";
  // Music/rhythm/sound beats VISUALIZE the phenomenon (beat pulse / waveform /
  // notation / metronome) — never a repeated instrument image or a bare rail.
  if (generic && RHYTHM_RE.test(text)) return "rhythm";
  return kind;
}

// ── choose a visual + labels deterministically when the Director doesn't ──
export function pickVisualKind(role: string, text: string): VisualKind {
  if (role === "hook" || role === "cta") return "none";
  const s = text.toLowerCase();
  // Real-subject actors first. An explicit formula (H2O, CO2) is a strong signal…
  if (findMoleculeByFormula(text)) return "molecule";
  // …an element needs atomic context (bare symbols like "He" are too ambiguous).
  if (/\batoms?\b|\belements?\b|electron|proton|neutron|nucleus|periodic/.test(s) && findElement(text)) return "atom";
  if (/\bcycle|loop|repeat|feedback|season|orbit|circular|round\b/.test(s)) return "cycle";
  // A molecule by NAME needs chemistry context ("water cycle" must stay a cycle).
  if (/molecul|chemical|compound|\bbonds?\b|reaction|formula|chemistry/.test(s) && findMoleculeByName(text)) return "molecule";
  if (/kubectl|terminal|command line|\bbash\b|\bshell\b|\bcli\b|linux command|\bgit\b|docker|npm install|deploy.*command/.test(s)) return "terminal";
  if (ENGINE_RE.test(s)) return "engine";
  if (BIO_RE.test(s)) return "bio";
  if (CIRCUIT_RE.test(s)) return "circuit";
  if (GEOMETRY_RE.test(s)) return "geometry";
  if (NEURAL_RE.test(s)) return "neural";
  if (/\bconnect|network|synapse|spread|social|web|link|relationship|node\b/.test(s)) return "network";
  if (/\bgrow|increase|rise|fall|drop|trend|data|percent|number|rate|more|less|double|compound\b/.test(s)) return "grow";
  if (/\bversus|vs\.?|compare|difference|either|both|balance\b/.test(s)) return "compare";
  return "flow";
}

/** Derive 2–4 short node labels from a title when the Director gives none. */
export function deriveItems(title: string, kind: VisualKind): string[] {
  if (kind === "atom") {
    const el = findElement(title);
    return [el ? el.name : "Carbon"];
  }
  if (kind === "molecule") {
    const mol = findMolecule(title);
    return [mol ? mol.formula : "H2O"];
  }
  if (kind === "terminal") {
    return ["$ echo 'follow along'", "follow along"];
  }
  if (kind === "image" || kind === "sticker") {
    return [title]; // items[0] = the subject to depict
  }
  if (kind === "engine") {
    return [/\b(2|two)[\s-]?stroke/i.test(title) ? "2-stroke" : "4-stroke"];
  }
  if (kind === "bio") {
    return [parseBioConfig(title).subject];
  }
  if (kind === "circuit") {
    const c = parseCircuitConfig(title);
    return [c.component, c.state];
  }
  if (kind === "geometry") {
    return [parseGeometryConfig(title).subject];
  }
  if (kind === "neural") {
    return [parseNeuralConfig(title).subject];
  }
  if (kind === "mascot") {
    return [parseMascotConfig(title).pose, ...title.split(/\s+/).filter(Boolean).slice(0, 8)];
  }
  const stop = new Set(["the", "a", "an", "of", "to", "and", "or", "is", "are", "in", "on", "how", "why", "what", "your", "you", "it", "its", "for", "with"]);
  const words = title.split(/\s+/).map((w) => w.replace(/[^\w-]/g, "")).filter((w) => w && !stop.has(w.toLowerCase()));
  const picked = words.slice(0, kind === "cycle" ? 4 : 3);
  if (picked.length >= 2) return picked.map((w) => w[0].toUpperCase() + w.slice(1));
  return kind === "grow" ? ["Now", "Later"] : ["Input", "Process", "Result"];
}

interface VProps {
  items: string[];
  box: Box;
  theme: Theme;
  unit: number;
  t: number;
}

// ── shared node chip ──────────────────────────────────────────────────────
const Chip: React.FC<{ cx: number; cy: number; w: number; h: number; label: string; theme: Theme; unit: number; on?: number }> = ({
  cx, cy, w, h, label, theme, unit, on = 1,
}) => {
  const icon = iconForLabel(label); // real symbol (motor, server, brain…) when the label names one
  const x = cx - w / 2, y = cy - h / 2, r = h * 0.28;
  return (
    <g>
      {/* soft drop-shadow so the card reads as raised on ANY background */}
      <rect x={x} y={y + h * 0.055} width={w} height={h} rx={r} fill="#000000" opacity={theme.dark ? 0.35 : 0.14} />
      {/* SOLID card — surface2 is distinct from the surface/background in every
          theme, so the box is always clearly visible (was near-invisible white-
          on-white at 6% opacity). */}
      <rect
        x={x} y={y} width={w} height={h} rx={r}
        fill={theme.palette.surface2}
        stroke={theme.palette.primary}
        strokeWidth={lerp(1.5, 3, on)}
        strokeOpacity={0.5 + 0.4 * on}
      />
      {icon && (
        <Icon k={icon} cx={cx} cy={cy - h * 0.17} size={h * 0.44} color={theme.palette.primary} opacity={0.75 + 0.25 * on} />
      )}
      <text
        x={cx} y={icon ? cy + h * 0.26 : cy} textAnchor="middle" dominantBaseline="central"
        fontFamily={theme.body.family} fontWeight={700} fontSize={unit * (icon ? 2.0 : 2.3)}
        fill={theme.palette.ink} opacity={0.7 + 0.3 * on}
      >
        {trunc(label, 14)}
      </text>
    </g>
  );
};

// ── FLOW: boxes connected along an axis with tokens continuously flowing ───
const Flow: React.FC<VProps> = ({ items, box, theme, unit, t }) => {
  const nodes = items.slice(0, 4);
  const n = Math.max(2, nodes.length);
  const vertical = box.h > box.w * 0.8; // tall frames flow top→bottom
  const bw = vertical ? Math.min(box.w * 0.74, unit * 44) : Math.min(box.w / n - unit * 2, unit * 26);
  const bh = unit * 8.5;
  const pad = bh * 0.85;
  const centers = nodes.map((_, i) => {
    const f = n === 1 ? 0.5 : i / (n - 1);
    return vertical
      ? { x: box.cx, y: lerp(box.y + pad, box.y + box.h - pad, f) }
      : { x: lerp(box.x + bw / 2, box.x + box.w - bw / 2, f), y: box.cy };
  });
  const speed = 0.55;
  const arrow = unit * 1.6;
  return (
    <g>
      {/* connectors + flowing tokens + arrowhead */}
      {centers.slice(0, -1).map((c, i) => {
        const d = centers[i + 1];
        const ax = vertical ? c.x : c.x + bw / 2;
        const ay = vertical ? c.y + bh / 2 : c.y;
        const bx = vertical ? d.x : d.x - bw / 2;
        const by = vertical ? d.y - bh / 2 : d.y;
        const arrowPts = vertical
          ? `${bx - arrow},${by - arrow} ${bx + arrow},${by - arrow} ${bx},${by}`
          : `${bx - arrow},${by - arrow} ${bx - arrow},${by + arrow} ${bx},${by}`;
        return (
          <g key={`e${i}`}>
            <line x1={ax} y1={ay} x2={bx} y2={by} stroke={theme.palette.muted} strokeWidth={2.5} strokeOpacity={0.45} strokeDasharray="3 7" strokeLinecap="round" />
            <polygon points={arrowPts} fill={theme.palette.muted} opacity={0.5} />
            {[0, 1, 2, 3].map((j) => {
              const f = (t * speed + j / 4 + i * 0.1) % 1;
              const p = pointOnLine(ax, ay, bx, by, f);
              const glow = Math.sin(f * Math.PI);
              return <circle key={j} cx={p.x} cy={p.y} r={unit * (1.1 + 0.7 * glow)} fill={theme.palette.primary} opacity={0.3 + 0.7 * glow} />;
            })}
          </g>
        );
      })}
      {centers.map((c, i) => {
        const appear = clamp01((t - i * 0.22) / 0.5);
        const pulse = 1 + 0.025 * Math.sin(t * 2.4 + i);
        const slide = vertical ? (1 - appear) * unit * 3 : 0;
        return (
          <g key={`n${i}`} transform={`translate(0 ${slide}) translate(${c.x} ${c.y}) scale(${pulse * (0.7 + 0.3 * appear)}) translate(${-c.x} ${-c.y})`} opacity={appear}>
            <Chip cx={c.x} cy={c.y} w={bw} h={bh} label={nodes[i]} theme={theme} unit={unit} on={0.5 + 0.5 * Math.sin(t * 1.5 + i)} />
          </g>
        );
      })}
    </g>
  );
};

// A "hub" label names a central entity the others connect TO — so we lay it out
// hub-and-spoke (freeCodeCamp's "Giant Router", ByteByteGo's origin server),
// meaningful topology instead of a random ring of dots.
export const HUB_RE = /\b(server|router|hub|origin|internet|core|central|database|\bdb\b|cloud|controller|master|broker|gateway|switch|host|data ?cent(er|re)|mainframe|\bapi\b|backend|kernel|nucleus|sun|heart|cpu|processor)\b/i;

// ── NETWORK: hub-and-spoke when there's a hub, else a clean labeled ring ──────
const Network: React.FC<VProps> = ({ items, box, theme, unit, t }) => {
  const labels = items.map((s) => (s ?? "").trim()).filter(Boolean).slice(0, 6);
  const cx = box.cx, cy = box.cy;
  const R = Math.min(box.w, box.h) * 0.34;
  const P = theme.palette;
  const hubIdx = labels.findIndex((l) => HUB_RE.test(l));

  // ── hub-and-spoke ──
  if (hubIdx >= 0 && labels.length >= 3) {
    const hub = labels[hubIdx];
    const spokes = labels.filter((_, i) => i !== hubIdx);
    const nodes = spokes.map((l, i) => {
      const a = (i / spokes.length) * TAU - Math.PI / 2;
      return { x: cx + Math.cos(a) * R, y: cy + Math.sin(a) * R, l };
    });
    const hubIcon = iconForLabel(hub);
    const hubR = unit * 4.2;
    return (
      <g>
        {nodes.map((nd, i) => {
          const outward = i % 2 === 0;                 // alternate request / response
          const f = (t * 0.7 + i * 0.17) % 1;
          const p = pointOnLine(cx, cy, nd.x, nd.y, outward ? f : 1 - f);
          const appear = clamp01((t - i * 0.12) / 0.5);
          return (
            <g key={`e${i}`} opacity={appear}>
              <line x1={cx} y1={cy} x2={nd.x} y2={nd.y} stroke={P.primary} strokeWidth={1.5} strokeOpacity={0.3} />
              <rect x={p.x - unit * 0.7} y={p.y - unit * 0.7} width={unit * 1.4} height={unit * 1.4} rx={unit * 0.3}
                fill={P.highlight} opacity={0.45 + 0.55 * Math.sin(f * Math.PI)} />
            </g>
          );
        })}
        {nodes.map((nd, i) => {
          const appear = clamp01((t - i * 0.12) / 0.5);
          const icon = iconForLabel(nd.l);
          const r = unit * 3;
          return (
            <g key={`s${i}`} opacity={appear}>
              <circle cx={nd.x} cy={nd.y} r={r} fill={P.surface2} stroke={P.secondary} strokeWidth={1.5} />
              {icon ? <Icon k={icon} cx={nd.x} cy={nd.y} size={r * 1.4} color={P.secondary} />
                : <circle cx={nd.x} cy={nd.y} r={r * 0.5} fill={P.secondary} />}
              <text x={nd.x} y={nd.y + r + unit * 1.9} textAnchor="middle" fontFamily={theme.body.family} fontWeight={700} fontSize={unit * 1.8} fill={P.ink} opacity={0.85}>{trunc(nd.l, 12)}</text>
            </g>
          );
        })}
        <g>
          <circle cx={cx} cy={cy} r={hubR * (1 + 0.06 * Math.sin(t * 2))} fill={P.primary} opacity={0.14} />
          <circle cx={cx} cy={cy} r={hubR} fill={P.surface} stroke={P.primary} strokeWidth={2} />
          {hubIcon ? <Icon k={hubIcon} cx={cx} cy={cy} size={hubR * 1.4} color={P.primary} />
            : <circle cx={cx} cy={cy} r={hubR * 0.5} fill={P.primary} />}
          <text x={cx} y={cy + hubR + unit * 2.2} textAnchor="middle" fontFamily={theme.body.family} fontWeight={800} fontSize={unit * 2.1} fill={P.ink}>{trunc(hub, 14)}</text>
        </g>
      </g>
    );
  }

  // ── no hub: a clean, even labeled ring with packets travelling the edges ──
  const nodes = labels.map((l, i) => {
    const a = (i / Math.max(1, labels.length)) * TAU - Math.PI / 2;
    return { x: cx + Math.cos(a) * R, y: cy + Math.sin(a) * R, l };
  });
  const edges: [number, number][] = [];
  for (let i = 0; i < nodes.length; i++) edges.push([i, (i + 1) % nodes.length]);
  return (
    <g>
      {edges.map(([a, b], i) => {
        const na = nodes[a], nb = nodes[b];
        if (a === b) return null;
        const f = (t * 0.6 + i * 0.2) % 1;
        const p = pointOnLine(na.x, na.y, nb.x, nb.y, f);
        return (
          <g key={i}>
            <line x1={na.x} y1={na.y} x2={nb.x} y2={nb.y} stroke={P.primary} strokeWidth={1.5} strokeOpacity={0.25} />
            <circle cx={p.x} cy={p.y} r={unit * 0.9} fill={P.primary} opacity={0.4 + 0.6 * Math.sin(f * Math.PI)} />
          </g>
        );
      })}
      {nodes.map((nd, i) => {
        const glow = 0.5 + 0.5 * Math.sin(t * 2 + i * 1.3);
        const appear = clamp01((t - i * 0.18) / 0.5);
        const r = unit * 3.2;
        const icon = iconForLabel(nd.l);
        return (
          <g key={`n${i}`} opacity={appear}>
            <circle cx={nd.x} cy={nd.y} r={r * (1 + 0.15 * glow)} fill={P.primary} opacity={0.12 + 0.18 * glow} />
            {icon ? <Icon k={icon} cx={nd.x} cy={nd.y} size={r * 1.5} color={P.primary} opacity={0.65 + 0.35 * glow} />
              : <circle cx={nd.x} cy={nd.y} r={r * 0.55} fill={P.primary} opacity={0.6 + 0.4 * glow} />}
            <text x={nd.x} y={nd.y + r + unit * 2} textAnchor="middle" fontFamily={theme.body.family} fontWeight={700} fontSize={unit * 2.1} fill={P.ink} opacity={0.8}>{trunc(nd.l, 12)}</text>
          </g>
        );
      })}
    </g>
  );
};

// ── CYCLE: stages around a ring, a highlight sweeping continuously ────────
const Cycle: React.FC<VProps> = ({ items, box, theme, unit, t }) => {
  const stages = items.slice(0, 4);
  const n = Math.max(2, stages.length);
  const cx = box.cx, cy = box.cy;
  const R = Math.min(box.w, box.h) * 0.34;
  const sweep = (t * 0.28) % 1;
  return (
    <g>
      <circle cx={cx} cy={cy} r={R} fill="none" stroke={theme.palette.muted} strokeWidth={2} strokeOpacity={0.3} strokeDasharray="3 7" />
      {/* sweeping arc head */}
      {(() => {
        const a = sweep * TAU - Math.PI / 2;
        return <circle cx={cx + Math.cos(a) * R} cy={cy + Math.sin(a) * R} r={unit * 1.4} fill={theme.palette.primary} />;
      })()}
      {stages.map((s, i) => {
        const a = (i / n) * TAU - Math.PI / 2;
        const x = cx + Math.cos(a) * R, y = cy + Math.sin(a) * R;
        const active = ((sweep * n) % n);
        const on = Math.max(0, 1 - Math.min(Math.abs(active - i), n - Math.abs(active - i)) * 1.2);
        const appear = clamp01((t - i * 0.2) / 0.5);
        return (
          <g key={i} opacity={appear}>
            <circle cx={x} cy={y} r={unit * (3 + 1.4 * on)} fill={theme.palette.primary} opacity={0.15 + 0.5 * on} />
            <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontFamily={theme.body.family} fontWeight={800} fontSize={unit * 2.1} fill={on > 0.5 ? theme.palette.ink : theme.palette.sub}>
              {i + 1}
            </text>
            <text x={x} y={y + unit * (4.6 + 1.4 * on)} textAnchor="middle" fontFamily={theme.body.family} fontWeight={700} fontSize={unit * 2} fill={theme.palette.ink} opacity={0.5 + 0.5 * on}>
              {trunc(s, 12)}
            </text>
          </g>
        );
      })}
    </g>
  );
};

// ── GROW: bars grow + a trend line draws on + a travelling dot ────────────
const Grow: React.FC<VProps> = ({ items, box, theme, unit, t }) => {
  const labels = items.slice(0, 5);
  const n = Math.max(3, labels.length);
  const baseY = box.y + box.h * 0.82;
  const top = box.y + box.h * 0.14;
  const left = box.x + box.w * 0.12;
  const right = box.x + box.w * 0.88;
  const vals = Array.from({ length: n }, (_, i) => 0.35 + 0.6 * rnd(i, 5) * ((i + 1) / n));
  const grow = easeInOut(clamp01(t / 1.3));
  const bw = ((right - left) / n) * 0.5;
  const xs = Array.from({ length: n }, (_, i) => lerp(left, right, n === 1 ? 0.5 : i / (n - 1)));
  const pts = xs.map((x, i) => ({ x, y: lerp(baseY, lerp(baseY, top, vals[i]), grow) }));
  const drawn = clamp01((t - 0.3) / 1.4);
  const path = pts.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const lead = pts[Math.min(pts.length - 1, Math.floor(drawn * (pts.length - 1)))];
  return (
    <g>
      <line x1={left - unit} y1={baseY} x2={right + unit} y2={baseY} stroke={theme.palette.muted} strokeWidth={2} strokeOpacity={0.4} />
      {pts.map((p, i) => (
        <g key={i}>
          <rect x={p.x - bw / 2} y={p.y} width={bw} height={baseY - p.y} rx={bw * 0.18} fill={theme.palette.primary} opacity={0.18 + 0.12 * Math.sin(t * 2 + i)} />
          {labels[i] && <text x={p.x} y={baseY + unit * 2.4} textAnchor="middle" fontFamily={theme.body.family} fontWeight={700} fontSize={unit * 1.9} fill={theme.palette.ink} opacity={0.7}>{trunc(labels[i], 8)}</text>}
        </g>
      ))}
      <path d={path} fill="none" stroke={theme.palette.primary} strokeWidth={unit * 0.9} strokeLinecap="round" strokeLinejoin="round" pathLength={1} strokeDasharray={1} strokeDashoffset={1 - drawn} />
      {drawn > 0.02 && <circle cx={lead.x} cy={lead.y} r={unit * 1.3} fill={theme.palette.primary} />}
    </g>
  );
};

// ── ambient floating particles behind everything (organic life) ───────────
export const AmbientMotion: React.FC<{ frame: { w: number; h: number }; theme: Theme; t: number; count?: number }> = ({ frame, theme, t, count = 9 }) => (
  <g opacity={theme.dark ? 0.5 : 0.35} pointerEvents="none">
    {Array.from({ length: count }, (_, i) => {
      const bx = rnd(i, 2) * frame.w;
      const by = rnd(i, 4) * frame.h;
      const x = bx + Math.sin(t * (0.15 + rnd(i, 6) * 0.2) + i) * frame.w * 0.05;
      const y = by + Math.cos(t * (0.12 + rnd(i, 8) * 0.2) + i) * frame.h * 0.04;
      const r = (6 + rnd(i, 9) * 22) * (frame.w / 1080);
      return <circle key={i} cx={x} cy={y} r={r} fill={theme.palette.primary} opacity={0.05 + 0.05 * rnd(i, 11)} />;
    })}
  </g>
);

// ── choreography: play sub-scenes in sequence with a zoom hand-off ────────
export interface PhaseSpec {
  kind: VisualKind;
  items: string[];
}

/**
 * Acts a beat's story out in order: each phase gets an equal slice of the beat,
 * its actor re-runs its own build-on from local t=0, and phases hand off with a
 * quick fade+settle — "nucleons cluster → electrons orbit → atoms bond" instead
 * of one static diagram. Pure function of t.
 */
export function renderPhasedVisual(
  phases: PhaseSpec[],
  box: Box,
  theme: Theme,
  unit: number,
  t: number,
  dur: number,
): React.ReactNode {
  const n = Math.max(1, phases.length);
  const seg = Math.max(0.001, dur / n);
  const idx = Math.max(0, Math.min(n - 1, Math.floor(t / seg)));
  const localT = t - idx * seg;
  // Each phase ARRIVES with a spring settle (anim.ts) — a hand-off, not a fade.
  const enter = animSettle(localT, 0.45, 0.25);
  const scale = animSettleScale(localT, 0.45, 0.25);
  const phase = phases[idx];
  return (
    <g
      opacity={enter}
      transform={`translate(${box.cx} ${box.cy}) scale(${scale}) translate(${-box.cx} ${-box.cy})`}
    >
      {renderVisual(phase.kind, phase.items, box, theme, unit, localT)}
    </g>
  );
}

// ── dispatcher ────────────────────────────────────────────────────────────
export function renderVisual(
  kind: VisualKind,
  items: string[],
  box: Box,
  theme: Theme,
  unit: number,
  t: number,
  assetUri?: string,
): React.ReactNode {
  const p = { items, box, theme, unit, t };
  const seed = `${kind}-${(items[0] ?? "").replace(/[^a-z0-9]/gi, "").slice(0, 16)}`;
  if (kind === "image" || kind === "sticker") {
    if (!assetUri) return <AssetPending box={box} theme={theme} unit={unit} t={t} label={items[0] ?? "illustration"} />;
    return kind === "image"
      ? <ScenePlate href={assetUri} box={box} theme={theme} unit={unit} t={t} idSeed={seed} />
      : <StickerCutout href={assetUri} box={box} theme={theme} unit={unit} t={t} idSeed={seed} label={items[0]} />;
  }
  switch (kind) {
    case "flow": return <Flow {...p} />;
    case "network": return <Network {...p} />;
    case "cycle": return <Cycle {...p} />;
    case "grow": return <Grow {...p} />;
    case "compare": return <Flow {...p} items={items.slice(0, 2)} />;
    case "atom": return <AtomVisual {...p} />;
    case "molecule": return <MoleculeVisual {...p} />;
    case "engine": return <EngineActor {...p} />;
    case "bio": return <BioActor {...p} />;
    case "circuit": return <CircuitActor {...p} />;
    case "geometry": return <GeometryActor {...p} />;
    case "neural": return <NeuralActor {...p} />;
    case "pipeline": return <IsoPipelineActor {...p} />;
    case "archflow": return <ArchFlowActor {...p} />;
    case "rhythm": return <RhythmActor {...p} />;
    case "mascot": return <MascotActor {...p} />;
    // A chart renders ONLY with valid data (the honesty rule) — else the beat
    // falls back to the abstract Grow trend rather than showing invented numbers.
    case "chart": return parseChartSpec(items) ? <ChartActor {...p} /> : <Grow {...p} />;
    case "terminal": return <TerminalActor {...p} />;
    default: return null;
  }
}
